import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../lib/utils.js';

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-1 text-[13px] shadow-sm transition-colors',
      'placeholder:text-muted-foreground',
      'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0',
      'disabled:cursor-not-allowed disabled:opacity-50',
      '[&>span]:truncate [&>span]:text-left',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 opacity-70" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-[var(--radix-select-content-available-height)] min-w-[8rem] overflow-hidden rounded-md border border-border bg-card text-foreground shadow-md',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'w-[var(--radix-select-trigger-width)] data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn('p-1', position === 'popper' && 'h-[var(--radix-select-trigger-height)]')}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-xs font-semibold text-muted-foreground', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-[13px] outline-none',
      'focus:bg-accent focus:text-accent-foreground',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// ─── Convenience wrapper (options-list API) ───────────────────
//
// Maintains backward compatibility with the `options/value/onChange` pattern
// used by media-add-form and media-edit-form. Internally uses Radix Select.

export interface SelectOption {
  value: string;
  label: string;
}

// Sentinel used internally so Radix SelectItem can represent "no selection"
// without using '' (Radix disallows '' as a SelectItem value — it reserves ''
// to mean "clear the selection and show the placeholder").
const EMPTY_SENTINEL = '__empty__';

export interface SelectFieldProps {
  id?: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** When true, prepends a clear/empty option to the dropdown list. */
  allowEmpty?: boolean;
  /** Label shown for the clear option when allowEmpty is true. Defaults to '—'. */
  emptyLabel?: string;
  error?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  'data-testid'?: string;
}

function SelectField({
  id,
  options,
  value,
  onChange,
  placeholder,
  allowEmpty,
  emptyLabel = '—',
  error,
  disabled,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: SelectFieldProps) {
  // Derive the label from options so the trigger shows the selected text even
  // when SelectContent is closed. Radix only injects the label via a portal
  // from SelectItemText, which only mounts when the dropdown is open.
  const selectedLabel = options.find((o) => o.value === value)?.label;

  // Map the EMPTY_SENTINEL back to '' so callers always receive clean values.
  const handleValueChange = (v: string) => {
    onChange(v === EMPTY_SENTINEL ? '' : v);
  };

  // Radix expects value='' to mean "no selection / show placeholder".
  // When the stored value is '' we keep it as is for the root; Radix will show
  // the placeholder. We do NOT pass EMPTY_SENTINEL as the root value.
  return (
    <Select value={value} onValueChange={handleValueChange} disabled={disabled}>
      {/* Visually-hidden native select mirrors Radix state for test queries */}
      <select
        aria-hidden="true"
        tabIndex={-1}
        data-testid={testId ? `${testId}-native` : undefined}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{ display: 'none' }}
      >
        <option value="">{allowEmpty ? emptyLabel : ''}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <SelectTrigger
        id={id}
        aria-label={ariaLabel}
        data-testid={testId ? `${testId}-trigger` : undefined}
        className={error ? 'border-destructive focus:ring-destructive/30' : undefined}
      >
        {/* Wrap the derived label in an element rather than passing a bare string.
            React 19 warns ("Cannot use a ref on a React element as a container to
            createRoot/createPortal …") when a portal target (Radix's SelectValue
            span, into which SelectItemText portals the selected text) also has
            React *text* children. Rendering an element child instead of a string
            sidesteps that string-children check while preserving the closed-trigger
            label. When there is no selected value we render nothing so Radix shows
            the `placeholder` prop. */}
        <SelectValue placeholder={placeholder}>
          {selectedLabel != null ? <span>{selectedLabel}</span> : undefined}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {allowEmpty ? <SelectItem value={EMPTY_SENTINEL}>{emptyLabel}</SelectItem> : null}
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SelectField,
  EMPTY_SENTINEL,
};
